import {
  useGetAvigilonAccessConnectorDetailsQuery,
  useSetupAvigilonAccessConnectorMutation,
} from 'apollo/generated/client-operations';
import type { ConnectorDetailsDrawerProps } from 'containers/jobsiteConfiguration/tabs/connectors/drawers/connectorCatalogEntry';
import {
  ConnectorDetailsDrawerLayout,
  ConnectorDetailsDrawerQueryState,
  type ConnectorConnectionRecoveryAction,
} from 'containers/jobsiteConfiguration/tabs/connectors/drawers/shared/ConnectorDetailsDrawerLayout';
import React from 'react';
import { getAvigilonAccessSiteUrl } from './avigilonAccessUrls';

export function AvigilonAccessConnectorDetailsDrawer({
  connector,
  open,
  onClose,
  onAfterLeave,
  onUpdated,
}: ConnectorDetailsDrawerProps): React.ReactElement {
  const { definition } = connector;
  // Keep details available while the drawer exits so its transition can finish.
  const detailsQuery = useGetAvigilonAccessConnectorDetailsQuery({
    variables: { connectorId: connector.id },
    fetchPolicy: 'no-cache',
  });
  const accessConnector = detailsQuery.data?.avigilonAccessConnector ?? null;
  const avigilonSite = accessConnector?.avigilonSite;
  const siteUrl = getAvigilonAccessSiteUrl(
    accessConnector?.definition.organizationId,
    avigilonSite?.id,
  );
  const [retrySetup, retrySetupState] =
    useSetupAvigilonAccessConnectorMutation();

  if (!accessConnector)
    return (
      <ConnectorDetailsDrawerQueryState
        open={open}
        title={definition.displayName}
        onClose={onClose}
        onAfterLeave={onAfterLeave}
        loading={detailsQuery.loading}
        error={
          detailsQuery.error ??
          (!detailsQuery.loading
            ? 'Avigilon Access connector details were not returned'
            : undefined)
        }
      />
    );

  const connectionRecovery: ConnectorConnectionRecoveryAction = {
    loading: retrySetupState.loading,
    execute: () =>
      retrySetup({ variables: { input: { siteId: accessConnector.siteId } } }),
    successTitle: 'Avigilon connected',
    failureTitle: 'Could not connect Avigilon',
  };

  return (
    <ConnectorDetailsDrawerLayout
      connector={accessConnector}
      open={open}
      onClose={onClose}
      onAfterLeave={onAfterLeave}
      onUpdated={onUpdated}
      onRefreshDetails={detailsQuery.refetch}
      connectionRecovery={connectionRecovery}
      presentation={{
        title: definition.displayName,
        description: definition.connectedDescription,
        setting: {
          label: definition.settingsLabel,
          name: avigilonSite?.name,
          url: siteUrl,
        },
        statusMessages: {
          draft: 'Avigilon Access has not been connected.',
          disabled:
            'Avigilon Access is disabled. The connection is retained, but this connector will not sync until it is enabled.',
          connectionFailed:
            'Avigilon Access could not be connected. See the run history below for details.',
          connecting: 'Avigilon Access is connecting.',
          connected: 'Avigilon Access is connected and ready to enable.',
          enabling: 'Avigilon Access is being enabled.',
          enablementFailed:
            'Avigilon Access could not be enabled. See the run history below for details.',
        },
      }}
    />
  );
}
